<script setup lang="ts">
import { ref } from 'vue'
import AppSelectField from '@/components/AppSelectField.vue'
import AppColorPicker from '@/components/AppColorPicker.vue'
import FontSelectField from '@/features/project-font/components/FontSelectField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import StorySelectField from '@/components/StorySelectField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import { ENABLE_CODE_SCENE_NAME_SETTING } from '@/config/featureFlags'
import type { SettingsGeneralTabProps } from '@/features/workspace/settings/types'
import { truncateProjectName } from '@/utils/projectNameLimit'

const props = defineProps<SettingsGeneralTabProps>()

/** 仅用户主动点按后才允许编辑作品标题，避免其他控件更新时焦点误落到此处弹出键盘。 */
const projectNameEditing = ref(false)

function handleProjectNameInput(event: Event) {
  const input = event.target as HTMLInputElement | null
  const nextName = truncateProjectName(input?.value || '')
  props.projectNameDraftRef.value = nextName
  if (input && input.value !== nextName) {
    input.value = nextName
  }
}

function handleProjectNamePointerDown(): void {
  projectNameEditing.value = true
}

function handleProjectNameFocus(event: FocusEvent): void {
  if (projectNameEditing.value) return
  const input = event.target as HTMLInputElement | null
  input?.blur()
}

function handleProjectNameBlur(): void {
  projectNameEditing.value = false
}
</script>

<template>
  <div v-if="portraitLayout" class="settings-page general-page-portrait">
    <div class="settings-portrait-props general-portrait-props">
      <section class="setting-card">
        <div class="card-title">总设</div>

        <label class="field-block">
          <span class="field-label">作品标题</span>
          <input
            :value="projectNameDraftRef.value"
            class="cell-input wide"
            :readonly="!projectNameEditing"
            @pointerdown="handleProjectNamePointerDown"
            @focus="handleProjectNameFocus"
            @blur="handleProjectNameBlur"
            @input="handleProjectNameInput"
          />
          <span class="field-hint project-name-limit-hint">不超过15个汉字或30个英文</span>
        </label>

        <section class="inline-card">
          <div class="field-inline">
            <label><input v-model="totalSetting.IsTitle" type="checkbox" /> 跳过标题画面</label>
            <label><input v-model="totalSetting.Auto" type="checkbox" /> 自动进行</label>
          </div>
        </section>

        <label class="field-block">
          <span class="field-label">工程菜单界面</span>
          <AppSelectField v-model="totalSetting.MenuIndex" wide :options="menuOptions" />
        </label>

        <label class="field-block">
          <span class="field-label">起始剧情</span>
          <StorySelectField
            v-model="totalSetting.StartMapId"
            picker-title="选择起始剧情"
            placeholder="请选择剧情"
          />
        </label>

        <label v-if="ENABLE_CODE_SCENE_NAME_SETTING" class="field-block">
          <span class="field-label">代码场景名</span>
          <input v-model="totalSetting.MenuCodeSceneName" class="cell-input wide" placeholder="可选：代码场景名" />
        </label>

        <div class="field-static">当前作品分辨率 {{ screenWidth }} * {{ screenHeight }}</div>
      </section>

      <section class="setting-card">
        <div class="card-title">字体</div>

        <label class="field-block">
          <span class="field-label">选择字体</span>
          <FontSelectField
            :model-value="totalSetting.FontName"
            picker-title="选择默认字体"
            @update:model-value="updateDefaultFontPath"
          />
        </label>

        <label class="field-block">
          <span class="field-label">字体大小</span>
          <NumberStepperField
            v-model="totalSetting.FontSize"
            class="cell-input sm"
            :min="1"
            :max="60"
            :step="1"
          />
        </label>

        <div class="color-row">
          <label class="field-block compact">
            <span class="field-label">对话默认颜色</span>
            <div class="color-input-row">
              <AppColorPicker v-model="messageColorHexRef.value" />
              <input v-model="totalSetting.FontColor_MSG" class="cell-input wide" />
            </div>
          </label>
          <label class="field-block compact">
            <span class="field-label">UI默认颜色</span>
            <div class="color-input-row">
              <AppColorPicker v-model="uiColorHexRef.value" />
              <input v-model="totalSetting.FontColor_UI" class="cell-input wide" />
            </div>
          </label>
        </div>
      </section>

      <section class="setting-card">
        <div class="card-title">系统音效</div>
        <div class="sound-grid">
          <label class="field-block compact">
            <span class="field-label">确定音效</span>
            <ResourceSelectField
              :model-value="totalSetting.EnterSE.name"
              category="audio"
              display-mode="full"
              store-mode="mapped"
              fallback-folder-path="音效"
              picker-title="选择音频"
              @update:model-value="updateEnterSePath"
            />
          </label>
          <label class="field-block compact">
            <span class="field-label">鼠标经过音效</span>
            <ResourceSelectField
              :model-value="totalSetting.MoveSE.name"
              category="audio"
              display-mode="full"
              store-mode="mapped"
              fallback-folder-path="音效"
              picker-title="选择音频"
              @update:model-value="updateMoveSePath"
            />
          </label>
        </div>
      </section>
    </div>
  </div>

  <div v-else class="settings-page general-page">
    <div class="general-grid">
      <div class="general-column general-column--left">
        <section class="setting-card">
          <div class="card-title">总设</div>

          <label class="field-block">
            <span class="field-label">作品标题</span>
            <input
            :value="projectNameDraftRef.value"
            class="cell-input wide"
            :readonly="!projectNameEditing"
            @pointerdown="handleProjectNamePointerDown"
            @focus="handleProjectNameFocus"
            @blur="handleProjectNameBlur"
            @input="handleProjectNameInput"
          />
            <span class="field-hint project-name-limit-hint">不超过15个汉字或30个英文</span>
          </label>

          <section class="inline-card">
            <div class="field-inline">
              <label><input v-model="totalSetting.IsTitle" type="checkbox" /> 跳过标题画面</label>
              <label><input v-model="totalSetting.Auto" type="checkbox" /> 自动进行</label>
            </div>
          </section>

          <label class="field-block">
            <span class="field-label">工程菜单界面</span>
            <AppSelectField v-model="totalSetting.MenuIndex" wide :options="menuOptions" />
          </label>

          <label class="field-block">
            <span class="field-label">起始剧情</span>
            <StorySelectField
              v-model="totalSetting.StartMapId"
              picker-title="选择起始剧情"
              placeholder="请选择剧情"
            />
          </label>

          <label v-if="ENABLE_CODE_SCENE_NAME_SETTING" class="field-block">
            <span class="field-label">代码场景名</span>
            <input v-model="totalSetting.MenuCodeSceneName" class="cell-input wide" placeholder="可选：代码场景名" />
          </label>

          <div class="field-static">当前作品分辨率 {{ screenWidth }} * {{ screenHeight }}</div>
        </section>
      </div>

      <div class="general-column general-column--center">
        <section class="setting-card">
          <div class="card-title">字体</div>

          <label class="field-block">
            <span class="field-label">选择字体</span>
            <FontSelectField
              :model-value="totalSetting.FontName"
              picker-title="选择默认字体"
              @update:model-value="updateDefaultFontPath"
            />
          </label>

          <label class="field-block">
            <span class="field-label">字体大小</span>
            <NumberStepperField
              v-model="totalSetting.FontSize"
              class="cell-input sm"
              :min="1"
              :max="60"
              :step="1"
            />
          </label>

          <div class="font-preview" :style="{ fontFamily: previewFont, fontSize: previewSize }">橙光</div>

          <div class="color-row">
            <label class="field-block compact">
              <span class="field-label">对话默认颜色</span>
              <div class="color-input-row">
                <AppColorPicker v-model="messageColorHexRef.value" />
                <input v-model="totalSetting.FontColor_MSG" class="cell-input wide" />
              </div>
            </label>
            <label class="field-block compact">
              <span class="field-label">UI默认颜色</span>
              <div class="color-input-row">
                <AppColorPicker v-model="uiColorHexRef.value" />
                <input v-model="totalSetting.FontColor_UI" class="cell-input wide" />
              </div>
            </label>
          </div>

        </section>

      </div>

      <div class="general-column general-column--right">
        <section class="setting-card">
          <div class="card-title">系统音效</div>
          <div class="sound-grid">
            <label class="field-block compact">
              <span class="field-label">确定音效</span>
              <ResourceSelectField
                :model-value="totalSetting.EnterSE.name"
                category="audio"
                display-mode="full"
                store-mode="mapped"
                fallback-folder-path="音效"
                picker-title="选择音频"
                @update:model-value="updateEnterSePath"
              />
            </label>
            <label class="field-block compact">
              <span class="field-label">鼠标经过音效</span>
              <ResourceSelectField
                :model-value="totalSetting.MoveSE.name"
                category="audio"
                display-mode="full"
                store-mode="mapped"
                fallback-folder-path="音效"
                picker-title="选择音频"
                @update:model-value="updateMoveSePath"
              />
            </label>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.general-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1.05fr;
  gap: 8px;
  height: 100%;
  min-height: 0;
}

.general-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.general-page-portrait {
  padding: 0;
}

.general-portrait-props {
  flex: 1;
}

.project-name-limit-hint {
  white-space: nowrap;
}

@media (max-width: 980px) and (orientation: portrait) {
  .general-grid {
    grid-template-columns: 1fr;
  }
}
</style>
